<template>
  <div class="submission-lightbox"
    :class="{
      'submission-lightbox--small-display': smallDisplay
    }"
    :style="lightboxStyle"
    >
  <div class="submission-lightbox__constraint"
    @mousewheel.prevent.stop
    @click.self.stop="handleClose"
    @touchstart.self.prevent.stop="handleClose"
    :style="constraintStyle">
    <div class="submission-lightbox__wrapper"
      :style="wrapperStyle">
    <outline-button
      v-if="!smallDisplay"
      class="submission-lightbox__increment__button submission-lightbox__increment__button--prev"
      buttonIcon="chevron_left"
      iconOnly
      @click="stepSubmission({ direction: 'prev', navigate: true })"
      :disabled="!canPreviousSubmission">
    </outline-button>
    <outline-button
      v-if="!smallDisplay"
      class="submission-lightbox__increment__button submission-lightbox__increment__button--next"
      iconOnly
      buttonIcon="chevron_right"
      @click="stepSubmission({ direction: 'next', navigate: true })"
      :disabled="!canNextSubmission">
    </outline-button>
    <component
      :is="submissionWrapper"
      class="submission-lightbox__submission"
      :src="safeSrc"
      :id="safeId"
      :muted="true"
      lightbox
      :messageType="submissionMessage"
      @close="handleClose"
    />
    <div
      class="submission-lightbox__footer"
      :class="{
        'submission-lightbox__footer--show-note': showVideoNote
      }"
      v-if="!submissionMessage">
      <div class="submission-lightbox__footer__video-note" v-if="showVideoNote">
        <span class="submission-lightbox__footer__video-note__label">{{videoNoteLabel}}: </span>
        {{videoNoteCopy}}
      </div>
      <outline-button
        v-if="!submissionReported"
        class="submission-lightbox__footer__report-button"
        buttonIcon="error"
        @click="handleReportClick">
        {{reportCtaCopy}}
      </outline-button>
      <div
        v-if="submissionReported"
        class="submission-lightbox__footer__report-message"
        v-html="reportSuccessCopy">
      </div>
    </div>
    </div>

  </div>
  </div>
</template>

<script>
/**
 * Lightbox component for viewing and swapping between submissions
 *
 * Submission expanded view
 */
import {
  mapMutations, mapGetters, mapState, mapActions
} from 'vuex';
import SubmissionImage from './../components/submission-image';
import VideoPlayer from './../components/video-player';
import SubmissionMessage from './../components/submission-message';
import SubmissionSize from './../mixins/submission-size';
import SubmissionNavigation from './../mixins/submission-navigation';
import {
  SUBMISSION_CATEGORIES,
  submissionStatuses,
  errors,
} from '../core/config';
import { SUBMISSION_VIEW_SET_LIGHTBOX_EXPANDED } from './../core/store/mutation-types';
import {
  CONTENT_SUBMISSION_SECTION_REPORT_ENTRY_CTA,
  CONTENT_SUBMISSION_SECTION_REPORT_SUCCESS,
  CONTENT_SUBMISSION_SECTION_VIDEO_NOTE_LABEL,
  CONTENT_SUBMISSION_SECTION_VIDEO_NOTE,
} from '../../design-tokens/build/dictionary';
import { SUBMISSION_VIEW_REPORT_SUBMISSION, SUBMISSION_VIEW_UPDATE_SUBMISSION_PARAM } from '../core/store/action-types';
import { getDimensionsFromAspectRatio } from '../core/utils';

export default {
  name: 'submission-lightbox',
  props: {
  },
  mixins: [SubmissionSize, SubmissionNavigation],
  components: {
    SubmissionImage,
    VideoPlayer,
    SubmissionMessage
  },
  data() {
    return {
      reportCtaCopy: CONTENT_SUBMISSION_SECTION_REPORT_ENTRY_CTA,
      reportSuccessCopy: CONTENT_SUBMISSION_SECTION_REPORT_SUCCESS,
      videoNoteLabel: CONTENT_SUBMISSION_SECTION_VIDEO_NOTE_LABEL,
      videoNoteCopy: CONTENT_SUBMISSION_SECTION_VIDEO_NOTE
    };
  },
  computed: {
    /**
     * Use the small display version
     */
    smallDisplay() {
      return this.mediaMedium || this.mediaSmall;
    },

    /**
     * Submission category
     *
     * @returns {String}
     */
    submissionVideoAspectRatio() {
      return this.activeSubmission && `${this.submissionDimensions.width}:${this.submissionDimensions.height}`;
    },

    /**
     * Submission category
     *
     * @returns {String}
     */
    submissionCategory() {
      return this.activeSubmission && this.activeSubmission.category;
    },

    /**
     * Submission status
     *
     * @returns {String}
     */
    submissionStatus() {
      return this.activeSubmission && this.activeSubmission.status;
    },

    /**
     * A different flag from status because we don't show a submissions status to visitors
     *
     * @returns {Boolean}
     */
    submissionReported() {
      return this.reportedThisSession.includes(this.safeId);
    },

    /**
     * There's an error if we don't have an active submission
     *
     * @returns {Boolean}
     */
    submissionError() {
      return !this.activeSubmission;
    },

    /**
     * message to display
     *
     * @returns {String}
     */
    submissionMessage() {
      switch (this.submissionStatus) {
        case submissionStatuses.PENDING:
        case submissionStatuses.FLAGGED:
        case submissionStatuses.REJECTED:
          // don't display submission, display message
          return this.submissionStatus;

        default:
          return this.submissionError && errors.SUBMISSION_SERVER_ERROR;
      }
    },

    /**
     * Show video note under videos
     *
     * @return {Boolean}
     */
    showVideoNote() {
      return this.submissionCategory === SUBMISSION_CATEGORIES.video;
    },

    /**
     * Whether to show the message display instead of the submission
     *
     * @returns {Boolean}
     */
    showMessage() {
      return !!this.submissionMessage;
    },

    /**
     * wrapper name for the component
     *
     * @returns {String}
     */
    submissionWrapper() {
      if (this.showMessage) {
        return 'submission-message';
      }

      // display submission
      if (this.submissionCategory === SUBMISSION_CATEGORIES.video) {
        return 'video-player';
      }
      return 'submission-image';
    },

    /**
     * video id changed for modal view for uniqueness.
     */
    parsedId() {
      return `${this.safeId}-modal`;
    },

    /**
     * Id if there's a submision
     *
     * @returns {string}
     */
    safeId() {
      return this.activeSubmission && this.activeSubmission.id;
    },

    /**
     * Id if there's a submision
     *
     * @returns {string}
     */
    safeSrc() {
      let entry;

      // using switch instead of map because I'm not sure about the API response consistency with FE
      switch (this.submissionCategory) {
        case SUBMISSION_CATEGORIES.video:
          entry = this.activeSubmission.video;
          break;
        case SUBMISSION_CATEGORIES.image:
          entry = this.activeSubmission.image;
          break;
        default:
          return null;
      }

      return entry && entry.url;
    },

    /**
     * The constraining box around the submissions
     */
    constraintStyle() {
      // const maxHeight = this.viewportHeight * NUMBER_HEIGHT_LIGHTBOX_MAX_RAW;
      // const maxWidth = this.viewportWidth * NUMBER_WIDTH_LIGHTBOX_MAX_RAW;

      // return {
      //   maxHeight: `${maxHeight}px`,
      //   maxWidth: `${maxWidth}px`
      // };
      return {};
    },

    /**
     * Read the submission size so that we can resize
     */
    contentDimensions() {
      if (this.showMessage || !this.activeSubmission || !this.activeSubmission.aspect_ratio) {
        return this.defaultSubmissionDimensions;
      }

      const {
        width,
        height,
      } = getDimensionsFromAspectRatio(this.activeSubmission.aspect_ratio) || {};

      if ((!height || !width) && this.activeSubmission.category === SUBMISSION_CATEGORIES.video) {
        // TODO: more ratios

        if (height > width) {
          return {
            height: 1600,
            width: 900
          };
        }
        return {
          height: 900,
          width: 1600
        };
      }

      if (!width || !height) {
        return this.defaultSubmissionDimensions;
      }

      return {
        height,
        width,
      };
    },

    ...mapGetters('submissionView', [
      'activeSubmission',
      'canNextSubmission',
      'canPreviousSubmission',
    ]),
    ...mapState('submissionView', [
      'reportedThisSession',
    ]),
    // ...mapGetters('windowMedia', [
    //   'viewportHeight',
    // ]),
    ...mapState('windowMedia', [
      'mediaMedium',
      'mediaSmall',
      // 'scrollTop',
    ]),
  },
  watch: {
    /**
     * if we switch display styles there's a possibility that the lightbox will get cut off by the new, shorter iframe.
     */
    smallDisplay(value) {
      if (value) {
        this.handleClose();
      }
    }
  },
  methods: {
    handleClose() {
      this.setLightboxExpanded({ open: false });
      this.updateRoute({ submissionId: null });
    },
    handleReportClick() {
      this.reportSubmission({ submissionId: this.safeId });
    },
    ...mapMutations({
      setLightboxExpanded: SUBMISSION_VIEW_SET_LIGHTBOX_EXPANDED,
    }),
    ...mapActions({
      reportSubmission: SUBMISSION_VIEW_REPORT_SUBMISSION,
      updateRoute: SUBMISSION_VIEW_UPDATE_SUBMISSION_PARAM
    })
  }
};
</script>

<style lang="scss">
.submission-lightbox {
  z-index: $z-index-lightbox;
  position: fixed;
  // top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $color-background-submission-lightbox;

  &__constraint {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    height: 100%;
    width: 100%;
    // padding: offsetValue($size-height-submission-lightbox-design-accent-large, -1 *  $size-margin-submission-lightbox-constraint-large)
    //   offsetValue($size-width-submission-lightbox-design-accent-large,  -1 *  $size-margin-submission-lightbox-constraint-large);
    padding: {
      top: offsetValue($size-height-submission-lightbox-design-accent-large, -1 *  $size-margin-top-submission-lightbox-constraint-large);
      left: offsetValue($size-width-submission-lightbox-design-accent-large,  -1 *  $size-margin-submission-lightbox-constraint-large);
      right: offsetValue($size-width-submission-lightbox-design-accent-large,  -1 *  $size-margin-submission-lightbox-constraint-large);
      bottom: offsetValue($size-height-submission-lightbox-design-accent-large, -1 *  $size-margin-bottom-submission-lightbox-constraint-large);
    }
  }

  &__wrapper {
    position: relative;

    // background: pink;

    /// TODO: temp, need a lightbox component or like a style
    .video-player {
      position: relative;
      top: initial;
      left: initial;
      &,
      &__video {
        height: 100%;
        width: 100%;
        max-width: none;
        max-height: none;
        background-color: $color-background-submission-submission-base;
      }
    }

    .submission-image {
      flex: 1 1 auto;
      max-height: 100%;
      background-color:$color-background-submission-submission-base;
    }
  }

  &__increment__button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    &--prev {
      right: 100%;
      margin-right: $size-margin-submission-lightbox-increment-button-large;

    }
    &--next {
      left: 100%;
      margin-left: $size-margin-submission-lightbox-increment-button-large;
    }
  }

  &__submission {
    background-color: #121a35;
  }

  &__footer {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    min-width: 100%;

    margin-top: $size-margin-submission-lightbox-footer-large;
    text-align: center;

    &--show-note {
      margin-top: offsetValue($size-margin-submission-lightbox-footer-large, $size-margin-top-submission-lightbox-video-note-large);
    }

    &__video-note {
      @include body-copy-small;
      &__label {
        font-weight: $font-weight-submission-lightbox-video-note-label;
        color: $color-text-submission-lightbox-video-note-label;
      }
      margin: {
        top: $size-margin-top-submission-lightbox-video-note-large;
        bottom: $size-margin-bottom-submission-lightbox-video-note-large;
      }
    }

    &__report-message {
      @include body-copy-small;
      color: $color-text-submission-lightbox-report;
      font-size: $size-text-submission-lightbox-report;

      // line-height: $size-lineheight-submission-lightbox-report;
    }
  }


.submission-lightbox__footer__report-button {
  span.rb-icon.rb-button-outline__icon--icon.material-icons {
    font-size: 2rem;
  }
}

  &--small-display {
    .submission-lightbox {
      &__constraint {
        // padding: offsetValue($size-height-submission-lightbox-design-accent-small, -1 *  $size-margin-submission-lightbox-constraint-small)
        //   offsetValue($size-width-submission-lightbox-design-accent-small,  -1 *  $size-margin-submission-lightbox-constraint-small);

        padding: {
          top: offsetValue($size-height-submission-lightbox-design-accent-small, -1 *  $size-margin-top-submission-lightbox-constraint-small);
          left: offsetValue($size-width-submission-lightbox-design-accent-small,  -1 *  $size-margin-submission-lightbox-constraint-small);
          right: offsetValue($size-width-submission-lightbox-design-accent-small,  -1 *  $size-margin-submission-lightbox-constraint-small);
          bottom: offsetValue($size-height-submission-lightbox-design-accent-small, -1 *  $size-margin-bottom-submission-lightbox-constraint-small);
        }

      }
    }
  }
}
</style>
